import { useState } from "react"
import { useTranslation } from "react-i18next"
import { fetchQuery, graphql, useLazyLoadQuery } from "react-relay"
import {
  legacyQmsQuestionnaireByIdQuery,
  legacyQmsQuestionnaireByIdQuery as LegacyQmsQuestionnaireQuery,
} from "@/modules/patients/detail/progress-note/components/questionnaires/__generated__/legacyQmsQuestionnaireByIdQuery.graphql"
import { LegacyQmsAnswer } from "@/modules/patients/detail/progress-note/components/questionnaires/questionnaire-answer"
import { RelayEnvironment } from "@/relay-environment"

export const questionnaireSummaryQuery = graphql`
  query legacyQmsQuestionnaireByIdQuery($questionnaireId: Int!) {
    qmsQuestionnaireSummaryById(questionnaireId: $questionnaireId) {
      questionnaire {
        version
        answers {
          qmsId
          question
          shortQuestion
          missedAnswers
          answer {
            text
          }
          clinicalDecisionSupport {
            recommendation
            criticality
          }
        }
      }
      tmc {
        aiRecommendation
        aiReasoning
      }
      useCase {
        name
      }
    }
  }
`
export const fetchQuestionnaireSummary = async (questionnaireId: number) => {
  return fetchQuery<LegacyQmsQuestionnaireQuery>(RelayEnvironment, questionnaireSummaryQuery, {
    questionnaireId,
  }).toPromise()
}

type QmsQuestionnaireProps = {
  questionnaireId: number
}

export const LegacyQmsQuestionnaire = ({ questionnaireId }: QmsQuestionnaireProps) => {
  const [showReasoning, setShowReasoning] = useState(false)
  const { t } = useTranslation()

  const data = useLazyLoadQuery<legacyQmsQuestionnaireByIdQuery>(
    questionnaireSummaryQuery,
    { questionnaireId },
    { fetchPolicy: "store-and-network" },
  )
  const { questionnaire, tmc } = data.qmsQuestionnaireSummaryById

  return (
    <>
      <div className="flex items-center justify-between">
        {tmc && (
          <div data-testid="ai-recommendation" className="flex flex-col py-1">
            <p className="text-on-surface text-sm">
              {t(`patient.questionnaire.aiRecommendation.${tmc.aiRecommendation}`)}
            </p>
            {tmc.aiReasoning.length > 0 && (
              <>
                {showReasoning && <p className="text-on-surface text-sm leading-5">{tmc.aiReasoning}</p>}
                <button
                  className="text-primary w-fit text-xs leading-4 tracking-[0.25px]"
                  onClick={() => setShowReasoning((prevState) => !prevState)}>
                  {showReasoning ? t("patient.questionnaire.hideReasoning") : t("patient.questionnaire.showReasoning")}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      <div className="mt-8 flex flex-col gap-4">
        {questionnaire?.answers.map((item) => (
          <LegacyQmsAnswer key={item.qmsId} item={item} />
        ))}
      </div>
    </>
  )
}
